import {
  BalanceData, CardsState, DAILY_BUY_ALL, DAILY_OFFER_IDS, DAILY_PAGE, DealIntent, DealsJson, DealsState, GearItem, GrantedItems, OfferItem, OfferPageDef,
  OfferPrice, OfferScreen, TRACK_REF, UpgradeJob, VipBenefits, VipRecord, VipRecordJson, VipTierProgress, gemPrices, grantItems, offerOf, pageOf,
  periodOf, builderOnSale, canFinishFree, finishFree, hireBuilder, nextBuilderOffer, purchasesAllowed, showsValue, utcDay, valuePercent, vipResources, vipTierProgress, vipXpForPrice, VIP_PACKS_PAGE, worthGems, itemGems,
} from "@highmarch/domain";
import type { LevelCatalog } from "./levelCatalog";
import { NOT_SAVING_HERE } from "./purchases";
import { Cards, cardsSection, mergeGranted } from "./cards";
import { HasSections, registerSaveSection } from "./saveSections";
import { Resolved, dealProducts, pageOfferIds, periodKeyAt, resolveDeal } from "./dealsResolve";
import { DealGrant, DealsContext, OfferView, PageView, TrackView } from "./dealsTypes";

const MS_PER_SECOND = 1000;
const SECONDS_PER_HOUR = 3600;

/**
 * The Deals screen's use cases (docs/SHOP.md §4; `LevelCatalog.deals`, granted through PurchaseService): which pages and offers the player sees, their honest value and VIP points, the picks and prizes, the
 * purchase intent recorded before the Store is asked, the grant when the Store's verified transaction arrives (once
 * per transaction id), the Weekly Deal track, and VIP. Real money moves only through the `Store` port
 * (purchases.ts); nothing is offered or sold outside the hub (the never list). The state lives in the `deals` and
 * `vip` save sections.
 */

function asObject<T>(v: unknown): T | undefined {
  return typeof v === "object" && v !== null && !Array.isArray(v) ? (v as T) : undefined;
}

export const dealsSection = registerSaveSection<DealsJson>("deals", asObject, (v) => v);
/** The `vip` section: VipRecord's v2 JSON; a pass-1 section (`{points, loginDay, chestDay}`) is read and migrated by VipRecord.fromJson. */
export const vipSection = registerSaveSection<VipRecordJson>("vip", asObject, (v) => v);

export interface DealsDeps {
  readonly catalog: LevelCatalog;
  readonly state: DealsState;
  readonly vip: VipRecord;
  /** The `cards` section (MP-01..03: cards and the fund); fresh when absent. */
  readonly cards?: CardsState;
  /** Cosmetics: whether a skin is owned, and unlocking one an offer paid. */
  ownsSkin(id: string): boolean;
  unlockSkin(id: string): void;
}

export class Deals {
  /** Cards and the growth fund (cards.ts): offers here, their days and milestones claimed there. */
  readonly cards: Cards;

  constructor(private readonly d: DealsDeps) {
    this.cards = new Cards({ catalog: d.catalog, state: d.cards ?? new CardsState(), pay: (items, ref, now) => this.pay(items, [], ref, now).granted });
  }

  private get balance(): BalanceData { return this.d.catalog.balance; }
  /** The balance data (the VIP page's view reads the VIP table and the Special Packs through it). */
  get balanceData(): BalanceData { return this.d.catalog.balance; }
  get state(): DealsState { return this.d.state; }
  /** The `vip` section: XP, the claim streak, the days it was last counted and claimed, and of the last daily bundle. */
  get vipRecord(): VipRecord { return this.d.vip; }
  /** The level offers are sized for: the highest cleared, at least 1. */
  get level(): number { return Math.max(1, this.d.catalog.highestLevelCleared); }

  /** Loads both sections from a save (fresh state when absent). */
  static stateFromSave(save: HasSections): { state: DealsState; vip: VipRecord; cards: CardsState } {
    return { state: DealsState.fromJson(dealsSection.read(save)), vip: VipRecord.fromJson(vipSection.read(save)), cards: CardsState.fromJson(cardsSection.read(save)) };
  }

  applyTo(save: HasSections): void {
    dealsSection.write(save, this.d.state.toJson());
    vipSection.write(save, this.d.vip.toJson());
    cardsSection.write(save, this.cards.state.toJson());
  }

  // ------------------------------------------------------------------ VIP

  /** The current VIP level's benefits (vip.json; all zero at VIP 0). */
  perks(): VipBenefits { return this.d.vip.benefits(this.balance.vip); }
  /** The VIP level, the XP and the way to the next level (`nextAt` null = Maxed). */
  vipProgress(): VipTierProgress { return vipTierProgress(this.balance.vip, this.d.vip.xp); }
  /** The VIP level (0..12). */
  vipLevel(): number { return this.d.vip.level(this.balance.vip); }

  /** Whether today's VIP XP waits to be claimed (docs/SHOP.md §8.2; owner 2026-10-24: claimed, not paid on the hub visit). */
  vipXpReady(now: number): boolean { return this.d.vip.claimReady(now); }

  /** Claims today's VIP XP on the VIP page (hub only): the claim streak's XP. Returns the XP added, or why not. */
  claimVipXp(now: number, screen: OfferScreen = "hub"): number | string {
    if (!purchasesAllowed(screen)) return "not here";
    if (!this.d.catalog.canPersist()) return NOT_SAVING_HERE;
    const added = this.d.vip.claimDaily(this.balance.vip, now);
    if (added <= 0) return "not ready";
    this.syncPerks();
    return added;
  }

  /** Adds VIP XP (a purchase, or the debug hook) and re-syncs the benefits. Returns the XP added. */
  addVipXp(xp: number): number {
    if (!Number.isInteger(xp) || xp <= 0) return 0;
    this.d.vip.addXp(xp);
    this.syncPerks();
    return xp;
  }

  /**
   * Hands the VIP level's benefits to the rules that read them (docs/SHOP.md §8.3): the profile (`profile.vip`: combat
   * through gearBonuses, build speed through durationFor, play reductions, queue slots, free finish), Tribute's extra
   * hours and resource production, extra order rerolls, extra crate slots. Derived from the XP, so never saved; the
   * catalog calls it on load and this class after every XP change. Deal changes and Merchant refreshes are read where used.
   */
  syncPerks(): void {
    const p = this.perks(), c = this.d.catalog;
    c.profile.vip = p;
    c.economy.tribute.extraCapSeconds = p.tributeExtraHours * SECONDS_PER_HOUR;
    c.economy.tribute.resourcePercent = p.resourcePercent;
    c.orders.extraRerolls = p.extraOrderRerolls;
    c.economy.shop.extraCrateSlots = p.extraCrateSlots;
  }

  /**
   * A resource amount the VIP level's production raises (docs/SHOP.md §8.3: Tribute and the level-end battle chest only;
   * never purchases, orders, check-in, events or shops): the gold to add, rounded down.
   */
  productionBonus(gold: number): number { return vipResources(gold, this.perks().resourcePercent) - gold; }

  /** Whether a running upgrade can be finished free by the VIP perk now: true, or why not. */
  canFinishFree(jobId: number, now: number): true | string { return canFinishFree(this.d.catalog.profile, jobId, now); }

  /** VIP free finish: a running upgrade with `freeFinishMinutes` or less left completes now. Returns what completed. */
  finishFree(jobId: number, now: number): UpgradeJob[] | string {
    const ok = finishFree(this.d.catalog.profile, jobId, now);
    return ok === true ? this.d.catalog.profile.claimCompleted(now) : ok;
  }
  /** Whether today's free VIP bundle waits (the level has one and it was not claimed this UTC day). */
  vipBundleReady(now: number): boolean { return this.d.vip.bundleReady(this.balance.vip, now); }

  /** Claims today's free VIP bundle (hub only): the current level's `dailyBundle`, paid like any grant (chests banked, gear bound). */
  claimVipBundle(now: number, screen: OfferScreen = "hub"): GrantedItems | string {
    if (!purchasesAllowed(screen)) return "not here";
    if (!this.d.catalog.canPersist()) return NOT_SAVING_HERE;
    const items = this.perks().dailyBundle;
    if (!this.d.vip.takeBundle(this.balance.vip, now)) return "not ready";
    return this.pay(items, [], `vip:${utcDay(now)}`, now).granted;
  }

  // ------------------------------------------------------------------ what is shown

  /**
   * The pages the player sees now, in catalog order: a page shows once its unlockAfterLevel is cleared and while it
   * has an offer on sale (the gem packs page always, when paid offers are allowed; its packs are PurchaseService's).
   */
  pages(now: number, ctx: DealsContext): PageView[] {
    if (!ctx.paidAllowed) return [];
    const out: PageView[] = [];
    for (const page of this.balance.offers.pages) {
      // The VIP Special Packs are the VIP page's, never a Deals tab (docs/SHOP.md §8.4).
      if (page.id === VIP_PACKS_PAGE || this.d.catalog.highestLevelCleared < page.unlockAfterLevel) continue;
      const offers = pageOfferIds(page).map((id) => this.view(id, now, ctx)).filter((v): v is OfferView => v !== null);
      if (offers.length === 0 && !page.gemPacks) continue;
      const d = page.daily;
      const theme = d ? this.d.state.dailyTheme(this.balance.offers, this.d.catalog.playerId, now) : "";
      const used = this.d.state.daily.day === utcDay(now) ? this.d.state.daily.changes : 0;
      out.push({
        // MP-05: the Welcome Back pack's page counts down its window, not a rotation.
        page, endsAt: page.offers.some((o) => o.welcomeBack) ? this.d.catalog.offerTriggers.welcomeEndsAt() : periodOf(page.rotation, now).endsAt, offers,
        theme: d ? { id: theme, name: d.themes.find((t) => t.id === theme)?.name ?? theme, changesLeft: this.d.state.dailyBoughtToday(now) ? 0 : Math.max(0, 1 + this.perks().extraDealChanges - used) } : null,
        track: page.track.length > 0 ? this.track(now) : null,
      });
    }
    return out;
  }

  /** One offer as shown now (daily.1..3 / daily.all included), or null when it is not on sale to this player. */
  view(offerId: string, now: number, ctx: DealsContext): OfferView | null {
    if (!ctx.paidAllowed) return null;
    const r = resolveDeal(this.balance, this.d.state, this.d.catalog.playerId, offerId, now);
    const cleared = this.d.catalog.highestLevelCleared;
    if (!r || cleared < r.page.unlockAfterLevel || cleared < r.levelTrigger) return null;
    // A VIP Special Pack is on sale from its VIP level (docs/SHOP.md §8.4).
    const def = offerOf(this.balance.offers, offerId), vipLevel = def?.vipLevel ?? 0;
    if (vipLevel > 0 && this.vipLevel() < vipLevel) return null;
    // A follow-on one-off (MP-04: Field Commander) is on sale once the offer it follows was bought.
    if (def && def.after !== "" && !this.bought(def.after, now)) return null;
    // MP-05: the Welcome Back pack is on sale only in the window after a return (offerTriggers.ts).
    if (def?.welcomeBack && !this.d.catalog.offerTriggers.welcomeOnSale(now)) return null;
    // docs/SHOP.md §18: builder n is on sale only while the player owns n − 1 (one at a time, real money only).
    if (def && !builderOnSale(def, this.d.catalog.profile.buildersOwned)) return null;
    // docs/SHOP.md §18: a ladder tier is on sale once the tier before it was bought this period; a bought tier steps aside for the next.
    const step = def ? this.ladderStep(def.pageId, offerId, now) : null;
    if (step && !step.current) return null;
    if (r.random && ctx.storefront !== null && this.balance.offers.noRandomStorefronts.includes(ctx.storefront)) return null;
    const remaining = this.d.state.remaining(offerId, r.limit, r.rotation, now);
    if (r.rotation === "once" && remaining <= 0) return null;
    const value = this.value(r.valueItems, r.price.cents), worth = r.page.valueStyle === "worth";
    return {
      offerId, pageId: r.page.id, name: r.name, price: r.price, wasCents: r.wasCents, items: r.items, picks: r.picks, choices: r.choices, prizes: r.prizes,
      prize: r.prize, gearSlotsNeeded: r.gearSlotsNeeded, gearSlots: r.gearSlots, remaining, valuePercent: value.percent, showValue: value.show && !worth,
      worthGems: worth ? worthGems(this.balance, r.valueItems, gemPrices(this.balance)) : null,
      vipPoints: this.vipPointsFor(r.price.cents), random: r.random, complete: r.complete, blocked: this.blocked(r, remaining, now),
      builder: def?.builder ?? 0, ladder: step ? { step: step.index + 1, of: step.of } : null,
    };
  }

  /**
   * Where an offer sits on its page's price ladder (docs/SHOP.md §18), or null off a ladder page: `current` is true for
   * the one tier on sale now — the first not bought this period, or the last tier once all are bought (shown sold out).
   */
  private ladderStep(pageId: string, offerId: string, now: number): { index: number; of: number; current: boolean } | null {
    const page = pageOf(this.balance.offers, pageId);
    if (!page || !page.ladder) return null;
    const ids = page.offers.map((o) => o.id), index = ids.indexOf(offerId);
    const firstOpen = ids.findIndex((id) => this.d.state.boughtNow(id, page.rotation, now) === 0);
    const current = firstOpen < 0 ? ids.length - 1 : firstOpen;
    return { index, of: ids.length, current: index === current };
  }

  /**
   * The next builder for sale (docs/SHOP.md §18: real money only, one after another), as the Store shows it, or null
   * when every builder is owned or paid offers are off. The Towers tab and the builder strip link to it.
   */
  nextBuilder(now: number, ctx: DealsContext): OfferView | null {
    const def = nextBuilderOffer(this.balance, this.d.catalog.profile.buildersOwned);
    return def ? this.view(def.id, now, ctx) : null;
  }

  private blocked(r: Resolved, remaining: number, now: number): string {
    if (remaining <= 0) return "sold out";
    const card = this.cards.blocked(r.offerId, now);
    if (card) return card;
    if (r.offerId === DAILY_BUY_ALL ? this.d.state.dailyBoughtToday(now) : false) return "buy all needs none bought today";
    if ((DAILY_OFFER_IDS as readonly string[]).includes(r.offerId) && this.d.state.boughtNow(DAILY_BUY_ALL, "daily", now) > 0) return "sold out";
    if (r.picks.some((p) => p < 0)) return "choose your picks";
    if (r.prizes.length > 0 && r.prize < 0) return "choose a prize";
    if (r.gearSlots.length < r.gearSlotsNeeded) return "choose a gear slot";
    if (!this.d.catalog.canPersist()) return NOT_SAVING_HERE;
    return "";
  }

  /**
   * The Deals tab badge: reached, unclaimed Weekly Deal milestones + today's VIP XP and a ready VIP bundle + each `badgeUntilSeen` page on
   * sale that Deals has not been opened on since (MP-04: First Command once level 3 is cleared) + each page an offer
   * trigger suggests (MP-05, offerTriggers.ts; quiet after a loss). A badge, never a push.
   */
  badge(now: number, ctx: DealsContext = { paidAllowed: true, storefront: null }): number {
    const t = this.track(now);
    const owed = t ? t.milestones.filter((m) => m.reached && !m.claimed).length : 0;
    const pages = new Set([...this.unseenPages(now, ctx), ...this.d.catalog.offerTriggers.suggestions(now, ctx).deals]);
    return owed + (this.vipXpReady(now) ? 1 : 0) + (this.vipBundleReady(now) ? 1 : 0) + this.cards.badge() + pages.size;
  }

  /** The `badgeUntilSeen` pages on sale now that Deals has not been opened on since (MP-04). */
  unseenPages(now: number, ctx: DealsContext): string[] {
    return this.pages(now, ctx).filter((p) => p.page.badgeUntilSeen && !this.d.state.seen.includes(p.page.id)).map((p) => p.page.id);
  }

  /** Deals was opened: its badged pages count as seen (the pill's badge clears). Returns whether anything changed. */
  noteOpened(now: number, ctx: DealsContext): boolean {
    const ids = this.unseenPages(now, ctx);
    for (const id of ids) this.d.state.markSeen(id);
    return ids.length > 0;
  }

  /** Whether an offer has been bought in its current period (a `once` offer: ever). */
  private bought(offerId: string, now: number): boolean {
    const o = offerOf(this.balance.offers, offerId), page = o ? pageOf(this.balance.offers, o.pageId) : null;
    return page !== null && this.d.state.boughtNow(offerId, page.rotation, now) > 0;
  }

  // ------------------------------------------------------------------ choices

  setPick(offerId: string, slot: number, choice: number): true | string {
    const o = offerOf(this.balance.offers, offerId);
    if (!o || slot < 0 || slot >= o.picks || choice < 0 || choice >= o.choices.length) return "no such pick";
    this.d.state.setPick(offerId, slot, choice);
    return true;
  }

  setPrize(offerId: string, prize: number): true | string {
    const o = offerOf(this.balance.offers, offerId);
    if (!o || prize < 0 || prize >= o.prizes.length) return "no such prize";
    this.d.state.setPrize(offerId, prize);
    return true;
  }

  /**
   * Owner 2026-10-21: a Master's prize or a Custom Chest slot not yet chosen is pre-picked with the best-worth choice
   * (never a gear pick: that one needs its slot chosen), so the price shows at once; the player can still change it.
   * Returns whether anything was set (the caller saves).
   */
  prefill(offerId: string, now: number, ctx: DealsContext): boolean {
    const v = this.view(offerId, now, ctx);
    if (!v || v.blocked === "sold out") return false;
    const prices = gemPrices(this.balance);
    const best = (list: readonly OfferItem[]): number => {
      let at = -1, top = -1;
      list.forEach((it, i) => {
        if (it.kind === "gear" && it.slot === "pick") return;
        const w = itemGems(this.balance, it, prices).toNumber();
        if (w > top) { top = w; at = i; }
      });
      return at;
    };
    let changed = false;
    if (v.prizes.length > 0 && v.prize < 0) { const i = best(v.prizes); if (i >= 0) { this.d.state.setPrize(offerId, i); changed = true; } }
    if (v.picks.some((p) => p < 0)) {
      const i = best(v.choices);
      if (i >= 0) v.picks.forEach((p, slot) => { if (p < 0) { this.d.state.setPick(offerId, slot, i); changed = true; } });
    }
    return changed;
  }

  setGearSlot(offerId: string, index: number, slot: number): true | string {
    if (!Number.isInteger(slot) || slot < 0 || slot >= this.balance.gear.slots.length || index < 0) return "no such slot";
    this.d.state.setGearSlot(offerId, index, slot);
    return true;
  }

  changeTheme(themeId: string, now: number): true | string {
    return this.d.state.changeTheme(this.balance.offers, now, themeId, this.perks().extraDealChanges);
  }

  // ------------------------------------------------------------------ buying

  /** The value of a list of items at a price, and whether to show it. */
  value(items: readonly OfferItem[], priceCents: number): { percent: number; show: boolean } {
    const percent = valuePercent(this.balance, items, priceCents, gemPrices(this.balance));
    return { percent, show: showsValue(this.balance, percent) };
  }

  /**
   * Checks an offer can be bought now and records what is shown as the purchase intent, BEFORE the Store is asked for
   * `intent.product` (so a late or re-delivered transaction grants exactly what was shown). Hub only; refused when
   * the save cannot be written, sold out, incomplete, random in a no-random storefront, or paid offers are off.
   */
  prepare(offerId: string, now: number, ctx: DealsContext, screen: OfferScreen = "hub"): DealIntent | string {
    if (!purchasesAllowed(screen)) return "not here";
    const v = this.view(offerId, now, ctx);
    if (!v) return "not on sale";
    if (v.blocked !== "") return v.blocked;
    const r = resolveDeal(this.balance, this.d.state, this.d.catalog.playerId, offerId, now)!;
    const intent: DealIntent = {
      product: r.price.product, offerId, pageId: r.page.id, periodKey: periodKeyAt(r.rotation, now), priceCents: r.price.cents,
      theme: r.theme, picks: [...r.picks], prize: r.prize, gearSlots: [...r.gearSlots], at: now,
    };
    this.d.state.pushIntent(intent);
    return intent;
  }

  /** Whether a store product id is a Deals product (else it is a gem pack or unknown: PurchaseService handles it). */
  knowsProduct(productId: string): boolean { return dealProducts(this.balance).has(productId); }

  /**
   * Grants a verified store transaction for a Deals product once (by transaction id): the oldest live intent for the
   * product, else the offer as it stands now with its cheapest choices; counts the purchase in the intent's period,
   * adds Weekly Deal tokens and VIP points, pays the items and banks the chests (their gear bound). The caller
   * (PurchaseService) persists, then finishes the transaction.
   */
  grant(t: { readonly transactionId: string; readonly productId: string; readonly purchasedAtMs: number }, now: number): DealGrant {
    const wallet = this.d.catalog.economy.wallet;
    // Once per transaction; a refund that arrived before the grant is recorded in the wallet too, and grants nothing.
    if (this.d.state.isGranted(t.transactionId) || wallet.purchase(t.transactionId) !== null) return { status: "duplicate" };
    const product = dealProducts(this.balance).get(t.productId);
    if (!product) return { status: "unknownProduct" };
    const at = Number.isFinite(t.purchasedAtMs) ? Math.floor(t.purchasedAtMs / MS_PER_SECOND) : now;
    const intent = this.d.state.takeIntent(t.productId, now);
    const r = resolveDeal(this.balance, this.d.state, this.d.catalog.playerId, product.offerId, intent?.at ?? at,
      intent ? { theme: intent.theme, picks: intent.picks, prize: intent.prize, gearSlots: intent.gearSlots } : {}, true);
    if (!r) return { status: "unknownProduct" };
    const periodKey = intent?.periodKey ?? periodKeyAt(r.rotation, at);
    for (const id of product.offerId === DAILY_BUY_ALL ? [...DAILY_OFFER_IDS, DAILY_BUY_ALL] : [product.offerId]) this.d.state.noteBought(id, periodKey);
    if (r.weeklyTokens > 0) this.addTokens(periodKey, r.weeklyTokens, now);
    const vipPoints = this.vipPointsFor(r.price.cents);
    this.addVipXp(vipPoints);
    const paid = this.pay(r.items, r.gearSlots, t.transactionId, now);
    // docs/SHOP.md §18: a builder offer hires its builder through the profile (queued upgrades start on it at once).
    const def = offerOf(this.balance.offers, product.offerId);
    const builders = def && def.builder > 0 ? hireBuilder(this.d.catalog.profile, def.builder, now) : 0;
    this.d.state.markGranted(t.transactionId);
    // The wallet's purchase record: a refund (PurchaseService) takes back at most the gems this paid, once (§4.2.4).
    wallet.recordPurchase(t.transactionId, t.productId, paid.granted.gems, at);
    // A card starts (or extends) and a fund pays the milestones already reached (cards.ts): one receipt.
    const extra = this.cards.granted(product.offerId, t.transactionId, at, now);
    this.d.catalog.offerTriggers.noteBought(product.offerId, at);   // MP-05: the Welcome Back pack spends its window
    return { status: "granted", offerId: product.offerId, granted: extra ? mergeGranted(paid.granted, extra) : paid.granted, vipPoints, bankedGear: paid.bankedGear, builders };
  }

  /** A refund (PurchaseService): a card's remaining days or a fund's milestones stop paying. */
  revoked(transactionId: string): boolean { return this.cards.revoked(transactionId); }

  /** Adds Weekly Deal tokens in `week`; moving to a new week first pays the old week's reached, unclaimed milestones. */
  private addTokens(week: number, n: number, now: number): void {
    const page = pageOf(this.balance.offers, "weekly");
    if (page && this.d.state.week.key !== week && this.d.state.week.key >= 0) {
      for (const tokens of this.d.state.owedMilestones(page)) {
        const items = this.d.state.claimMilestone(page, tokens);
        if (typeof items !== "string") this.pay(items, [], `${TRACK_REF}${this.d.state.week.key}:${tokens}`, now);
      }
    }
    this.d.state.addTokens(week, n);
  }

  // ------------------------------------------------------------------ Weekly Deal track

  track(now: number): TrackView | null {
    const page = this.d.state && pageOf(this.balance.offers, "weekly");
    if (!page || page.track.length === 0) return null;
    const tokens = this.d.state.tokens(now), current = this.d.state.week.key === periodOf("weekly", now).key;
    return { tokens, milestones: page.track.map((m) => ({ tokens: m.tokens, items: m.items, reached: tokens >= m.tokens, claimed: current && this.d.state.week.claimed.includes(m.tokens) })) };
  }

  /** Claims a reached milestone of this week's track (free; hub only). */
  claimMilestone(tokens: number, now: number, screen: OfferScreen = "hub"): GrantedItems | string {
    if (!purchasesAllowed(screen)) return "not here";
    if (!this.d.catalog.canPersist()) return NOT_SAVING_HERE;
    const page = pageOf(this.balance.offers, "weekly");
    if (!page) return "no track";
    if (this.d.state.week.key !== periodOf("weekly", now).key) return "not reached";
    const items = this.d.state.claimMilestone(page, tokens);
    if (typeof items === "string") return items;
    return this.pay(items, [], `${TRACK_REF}${this.d.state.week.key}:${tokens}`, now).granted;
  }

  // ------------------------------------------------------------------ shared

  /** Pays items, banks their chests (gear bound) and unlocks their skins. */
  protected pay(items: readonly OfferItem[], gearSlots: readonly number[], ref: string, now: number): { granted: GrantedItems; bankedGear: GearItem[] } {
    const c = this.d.catalog;
    const granted = grantItems(this.balance, { profile: c.profile, wallet: c.economy.wallet, playerId: c.playerId, level: this.level, at: now, ref, ownsSkin: (id) => this.d.ownsSkin(id) }, items, gearSlots);
    const bankedGear: GearItem[] = [];
    for (const chest of granted.chests) for (const g of c.bankChest(chest)) { g.bound = true; bankedGear.push(g); }
    for (const s of granted.skins) this.d.unlockSkin(s);
    return { granted, bankedGear };
  }

  /** VIP XP for a price (the bubble): vip.json xpPerDollar × whole dollars, rounded up. */
  vipPointsFor(priceCents: number): number { return vipXpForPrice(this.balance.vip, priceCents); }

  /**
   * VIP XP for a real-money purchase that is not a Deals offer (a gem pack, docs/SHOP.md §8.1: every purchase gives
   * points, the gem pack's bubble), `quantity` times; perks re-synced. The caller grants it once per transaction.
   * Refunds keep the points, as for Deals: VIP levels never drop. Returns the points added.
   */
  notePurchase(priceCents: number, quantity = 1): number {
    return this.addVipXp(this.vipPointsFor(priceCents) * Math.max(0, quantity));
  }
}
